import type { IconName } from '@hotjar/hotsauce';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { GenericFilter } from '../GenericFilter/GenericFilter';
import type { BasicListOption, LargeListFilterValue } from './GenericLargeListType';
import { GenericLargeListDropdown } from './GenericLargeListDropdown';
import type { AbstractSingleFilterPillsProps } from '../../SingleFilterType';
import {
  DEFAULT_MAX_DISPLAYED_ITEMS,
  getValueLabel,
} from '../../../../../utils/LabelFormatter/SingleLabelFormatter/GenericLabelFormatter/LargeListLabelFormatter';

export interface GenericLargeListFilterProps<T>
  extends AbstractSingleFilterPillsProps<LargeListFilterValue<T>> {
  filterName: string;
  iconName: IconName;
  optionList: readonly BasicListOption<T>[];
  // optionListIsLoading can be used in coordination with onDropdownShown to load more optionList when we open the dropdown
  optionListIsLoading?: boolean;
  maxDisplayedItems?: number;
  header?: ReactNode;
  placeholder?: string;
  isFunnelStep?: boolean;
}

export function GenericLargeListFilter<T extends string>({
  currentValue,
  autoFocus,
  optionalDefaultValue,
  onApply,
  onRemove,
  filterName,
  iconName,
  optionList = [],
  optionListIsLoading,
  showDropdownOnMount,
  onDropdownHidden,
  onDropdownShown,
  maxDisplayedItems = DEFAULT_MAX_DISPLAYED_ITEMS,
  header,
  placeholder,
  isFunnelStep,
}: GenericLargeListFilterProps<T>): JSX.Element {
  const optionMap = useMemo(() => {
    const map = new Map(optionList.map((item) => [item.value, item]));
    // We add an unknown option if some selected values doesn't exist
    // to avoid breaking the component
    (currentValue?.selectedValues ?? []).forEach((item) => {
      if (!map.get(item)) {
        map.set(item, { value: item, label: item.toString() });
      }
    });
    return map;
  }, [optionList, currentValue]);
  const handleApply = (close: () => void, newValue?: LargeListFilterValue<T>) => {
    onApply(newValue);
    close();
  };

  return (
    <GenericFilter
      filterName={filterName}
      label1={filterName}
      label2={getValueLabel(currentValue, optionList, maxDisplayedItems)}
      autoFocus={autoFocus}
      iconName={iconName}
      onRemove={onRemove}
      showDropdownOnMount={showDropdownOnMount}
      onDropdownHidden={onDropdownHidden}
      onDropdownShown={onDropdownShown}
      isFunnelStep={isFunnelStep}
      renderDropdown={({ close }) => {
        return (
          <GenericLargeListDropdown
            filterName={filterName}
            currentValue={currentValue}
            optionalDefaultValue={optionalDefaultValue}
            onApply={(newValue) => handleApply(close, newValue)}
            optionListIsLoading={optionListIsLoading}
            optionMap={optionMap}
            header={header}
            placeholder={placeholder}
            isFunnelStep={isFunnelStep}
          />
        );
      }}
    />
  );
}
